import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["calendar", "days", "monthLabel", "slotsContainer", "bookingForm", "startsAt", "selectedTimeDisplay", "timezoneLabel", "bookerPhone", "meetingTypeHint", "meetingTypeLabel", "calendarWarning"]
  static values = { slug: String, duration: Number, timezone: String }

  connect() {
    this.currentMonth = new Date()
    this.currentMonth.setDate(1)
    this.selectedDate = null
    this.ianaTimezone = this.timezoneToIana(this.timezoneValue)
    this.renderCalendar()
    this.updatePhoneRequirement()
  }

  renderCalendar() {
    const year = this.currentMonth.getFullYear()
    const month = this.currentMonth.getMonth()

    const monthNames = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"]
    this.monthLabelTarget.textContent = `${monthNames[month]} ${year}`

    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    let html = ""

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      html += '<div class="p-2"></div>'
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      const dateStr = this.formatDate(date)
      const isPast = date < today
      const isSelected = this.selectedDate === dateStr

      if (isPast) {
        html += `<div class="p-2 text-center text-sm text-support-light-400 rounded">${day}</div>`
      } else {
        const selectedClass = isSelected ? "bg-core-purple-600 text-white" : "hover:bg-core-purple-200 text-support-dark-700"
        html += `<button type="button" class="meet-btn-reset p-2 text-center text-sm rounded ${selectedClass}" data-action="click->meet-booking#selectDate" data-date="${dateStr}">${day}</button>`
      }
    }

    this.daysTarget.innerHTML = html
  }

  prevMonth() {
    this.currentMonth.setMonth(this.currentMonth.getMonth() - 1)
    const today = new Date()
    // Don't allow going before current month
    if (this.currentMonth < new Date(today.getFullYear(), today.getMonth(), 1)) {
      this.currentMonth.setMonth(this.currentMonth.getMonth() + 1)
      return
    }
    this.renderCalendar()
  }

  nextMonth() {
    this.currentMonth.setMonth(this.currentMonth.getMonth() + 1)
    this.renderCalendar()
  }

  selectDate(event) {
    const dateStr = event.currentTarget.dataset.date
    this.selectedDate = dateStr
    this.renderCalendar()
    this.fetchSlots(dateStr)
  }

  async fetchSlots(dateStr) {
    this.slotsContainerTarget.innerHTML = '<p class="text-support-light-600 text-sm">Loading available times...</p>'
    this.bookingFormTarget.classList.add("hidden")

    try {
      const response = await fetch(`/${this.slugValue}/available_times?date=${dateStr}`)
      const data = await response.json()

      if (data.calendar_unavailable && this.hasCalendarWarningTarget) {
        this.calendarWarningTarget.classList.remove("hidden")
      }

      if (data.slots && data.slots.length > 0) {
        const tz = this.ianaTimezone
        const tzAbbr = data.timezone_abbr || ""
        if (this.hasTimezoneLabelTarget && tzAbbr) {
          this.timezoneLabelTarget.textContent = `Times shown in ${tzAbbr}`
        }
        let html = '<div class="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">'
        data.slots.forEach(slot => {
          const time = new Date(slot.start)
          const timeStr = time.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz })
          html += `<button type="button" class="meet-btn-reset slot-btn px-3 py-2 text-sm border border-support-light-300 rounded hover:bg-core-purple-200 hover:border-core-purple-400 text-support-dark-700 text-center transition-colors" data-action="click->meet-booking#selectSlot" data-start="${slot.start}">${timeStr}</button>`
        })
        html += "</div>"
        this.slotsContainerTarget.innerHTML = html
      } else {
        this.slotsContainerTarget.innerHTML = '<p class="text-support-light-600 text-sm">No available times on this date.</p>'
      }
    } catch (error) {
      this.slotsContainerTarget.innerHTML = '<p class="text-alert-red-600 text-sm">Error loading available times. Please try again.</p>'
    }
  }

  selectSlot(event) {
    const startIso = event.currentTarget.dataset.start
    const startTime = new Date(startIso)

    // Highlight selected slot
    this.slotsContainerTarget.querySelectorAll(".slot-btn").forEach(btn => {
      btn.classList.remove("bg-core-purple-600", "text-white", "border-core-purple-600")
    })
    event.currentTarget.classList.add("bg-core-purple-600", "text-white", "border-core-purple-600")

    // Show booking form
    this.startsAtTarget.value = startIso
    const tz = this.ianaTimezone
    const dateStr = startTime.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: tz })
    const timeStr = startTime.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz })
    this.selectedTimeDisplayTarget.textContent = `${dateStr} at ${timeStr}`
    this.bookingFormTarget.classList.remove("hidden")
  }

  timezoneToIana(railsTz) {
    const mapping = {
      "Eastern Time (US & Canada)": "America/New_York",
      "Central Time (US & Canada)": "America/Chicago",
      "Mountain Time (US & Canada)": "America/Denver",
      "Pacific Time (US & Canada)": "America/Los_Angeles",
      "Alaska": "America/Anchorage",
      "Hawaii": "Pacific/Honolulu",
      "Arizona": "America/Phoenix",
      "UTC": "UTC"
    }
    return mapping[railsTz] || "America/New_York"
  }

  toggleMeetingType(event) {
    const meetingType = event.currentTarget.value
    this.updatePhoneRequirement(meetingType)
    this.highlightSelectedMeetingType()
  }

  updatePhoneRequirement(meetingType) {
    if (!meetingType) {
      const checked = this.element.querySelector("input[name='meeting_type']:checked")
      meetingType = checked ? checked.value : "phone_call"
    }

    if (this.hasBookerPhoneTarget) {
      this.bookerPhoneTarget.required = (meetingType === "phone_call")
    }

    if (this.hasMeetingTypeHintTarget) {
      if (meetingType === "phone_call") {
        this.meetingTypeHintTarget.textContent = "We'll call you at the number above at the scheduled time."
      } else {
        this.meetingTypeHintTarget.textContent = "You'll receive a Zoom link in your confirmation email."
      }
    }
  }

  highlightSelectedMeetingType() {
    this.meetingTypeLabelTargets.forEach(label => {
      const radio = label.querySelector("input[type='radio']")
      if (radio && radio.checked) {
        label.classList.remove("border-support-light-300", "bg-white")
        label.classList.add("border-core-purple-600", "bg-core-purple-200")
      } else {
        label.classList.remove("border-core-purple-600", "bg-core-purple-200")
        label.classList.add("border-support-light-300", "bg-white")
      }
    })
  }

  formatDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }
}
